import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';

export interface InterestTag {
  id: string;
  icon: string;
  labelKey: string;
}

export const INTEREST_TAGS: InterestTag[] = [
  { id: 'temples', icon: '⛩️', labelKey: 'onboarding.interestTemples' },
  { id: 'food', icon: '🍜', labelKey: 'onboarding.interestFood' },
  { id: 'shopping', icon: '🛍️', labelKey: 'onboarding.interestShopping' },
  { id: 'nature', icon: '🌸', labelKey: 'onboarding.interestNature' },
  { id: 'nightlife', icon: '🌙', labelKey: 'onboarding.interestNightlife' },
  { id: 'culture', icon: '🎎', labelKey: 'onboarding.interestCulture' },
  { id: 'onsen', icon: '♨️', labelKey: 'onboarding.interestOnsen' },
  { id: 'anime', icon: '🎮', labelKey: 'onboarding.interestAnime' },
  { id: 'history', icon: '🏯', labelKey: 'onboarding.interestHistory' },
  { id: 'photography', icon: '📸', labelKey: 'onboarding.interestPhotography' },
  { id: 'adventure', icon: '🥾', labelKey: 'onboarding.interestAdventure' },
  { id: 'relaxation', icon: '🧘', labelKey: 'onboarding.interestRelaxation' },
];

interface InterestsStepProps {
  selected: string[];
  onToggle: (id: string) => void;
  isManga: boolean;
  isUkiyo: boolean;
}

export function InterestsStep({ selected, onToggle, isManga, isUkiyo }: InterestsStepProps) {
  const { t } = useTranslation('pages');

  return (
    <div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        {t('onboarding.selectInterests')}
      </p>
      <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
        {INTEREST_TAGS.map(tag => {
          const active = selected.includes(tag.id);
          const activeStyle: React.CSSProperties = isManga
            ? { borderRadius: 0, border: '2px solid #0a0a0a', background: 'rgba(0,0,0,0.06)' }
            : isUkiyo
              ? { borderRadius: 4, border: '1px solid rgba(184,134,11,0.8)', background: 'rgba(184,134,11,0.08)' }
              : { borderRadius: 12, border: '1px solid var(--color-primary-500, #2563eb)', background: 'rgba(37,99,235,0.06)' };
          const baseStyle: React.CSSProperties = isManga
            ? { borderRadius: 0, border: '2px solid transparent' }
            : isUkiyo
              ? { borderRadius: 4, border: '1px solid transparent' }
              : { borderRadius: 12, border: '1px solid transparent' };
          return (
            <button
              key={tag.id}
              type="button"
              onClick={() => onToggle(tag.id)}
              className={`relative flex flex-col items-center gap-1 p-3 text-center text-xs font-medium transition-colors ${
                active
                  ? 'text-gray-900 dark:text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
              style={active ? activeStyle : baseStyle}
              aria-pressed={active}
            >
              {active && (
                <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-success-500 text-white flex items-center justify-center">
                  <Check size={10} />
                </span>
              )}
              <span className="text-2xl" aria-hidden>{tag.icon}</span>
              <span className="leading-tight">{t(tag.labelKey)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
